import { resolveMatterResumeTarget } from './matterDesk';

const ROLE_META = {
  SENIOR_ADVOCATE: {
    label: 'Senior Advocate',
    home_label: 'Senior command view',
    daily_focus: 'Hearings, bench pressure, final arguments, and chamber delegation.',
  },
  AOR_CHAMBER_LEAD: {
    label: 'AOR / Chamber Lead',
    home_label: 'Chamber operations view',
    daily_focus: 'Listings, filing readiness, delegation, and client-safe movement across matters.',
  },
  JUNIOR_CLERK: {
    label: 'Junior / Clerk',
    home_label: 'Execution queue',
    daily_focus: 'Chronology, bundles, defects, task closure, and matter follow-through.',
  },
  INDEPENDENT_LITIGATOR: {
    label: 'Independent Litigator',
    home_label: 'Matter command view',
    daily_focus: 'Daily priorities, filings, hearings, and client communication in one place.',
  },
  FIRM_LAWYER: {
    label: 'Firm Lawyer',
    home_label: 'Disputes workflow view',
    daily_focus: 'Research, drafting, review, and cross-team matter continuity.',
  },
  CORPORATE_COUNSEL: {
    label: 'Corporate Counsel',
    home_label: 'Counsel operations view',
    daily_focus: 'Advice readiness, external counsel follow-up, and board-facing updates.',
  },
  STUDENT: {
    label: 'Student',
    home_label: 'Learning mode',
    daily_focus: 'Explain mode, briefs, IRAC, moot prep, and authority-led learning.',
  },
};

const LEGACY_ROLE_ALIASES = {
  SELF_PRACTITIONER: 'INDEPENDENT_LITIGATOR',
  CHAMBER_JUNIOR: 'JUNIOR_CLERK',
  LAW_FIRM_LAWYER: 'FIRM_LAWYER',
  ADVOCATE: 'INDEPENDENT_LITIGATOR',
  AOR: 'AOR_CHAMBER_LEAD',
};

const TRUST_GUIDE = [
  {
    id: 'verified_primary',
    label: 'Verified Primary',
    short_label: 'Primary',
    description: 'Official primary law or court text suitable for direct legal reliance.',
  },
  {
    id: 'verified_secondary',
    label: 'Verified Secondary',
    short_label: 'Secondary',
    description: 'Reporter-backed or verified secondary support that still needs correct forum use.',
  },
  {
    id: 'official_portal',
    label: 'Official Portal / Partial',
    short_label: 'Portal',
    description: 'Official portal material that may support status, metadata, or follow-up but not always final reasoning.',
  },
  {
    id: 'matter_file',
    label: 'Matter File',
    short_label: 'Matter',
    description: 'Private record from the live matter.',
  },
  {
    id: 'reviewed_memory',
    label: 'Reviewed Memory',
    short_label: 'Memory',
    description: 'Human-reviewed voice-derived matter memory.',
  },
  {
    id: 'external',
    label: 'External / Unverified',
    short_label: 'External',
    description: 'Unverified material that should not quietly drive conclusions.',
  },
  {
    id: 'inferred',
    label: 'Inferred',
    short_label: 'Inference',
    description: 'Reasoned synthesis rather than direct source text.',
  },
];

const DEFAULT_INTEGRATIONS = [
  {
    id: 'gmail',
    label: 'Gmail',
    surface: 'Email',
    mode: 'scaffolded',
    status_label: 'Scaffolded',
    description: 'Architecture is ready for client threads, filing reminders, and outbound updates once credentials are wired.',
  },
  {
    id: 'outlook',
    label: 'Outlook',
    surface: 'Email',
    mode: 'scaffolded',
    status_label: 'Scaffolded',
    description: 'Enterprise email boundaries are reserved for chambers and disputes teams using Outlook.',
  },
  {
    id: 'google_drive',
    label: 'Google Drive / DMS',
    surface: 'Documents',
    mode: 'manual',
    status_label: 'Manual / Ready',
    description: 'Document-source integration is scaffolded even when live sync is not yet connected.',
  },
  {
    id: 'word_docs',
    label: 'Word / Docs',
    surface: 'Drafting',
    mode: 'scaffolded',
    status_label: 'Scaffolded',
    description: 'Drafting workflows already reserve export/import boundaries for office-suite handoff.',
  },
  {
    id: 'calendar',
    label: 'Calendar / Reminders',
    surface: 'Scheduling',
    mode: 'manual',
    status_label: 'Manual / Ready',
    description: 'Court dates and follow-ups can be represented now and promoted to live sync later.',
  },
  {
    id: 'whatsapp',
    label: 'WhatsApp Delivery',
    surface: 'Client loop',
    mode: 'scaffolded',
    status_label: 'Scaffolded',
    description: 'Client-safe update surfaces are ready for delivery hooks once a provider is configured.',
  },
];

const DEFAULT_DELIVERY_SURFACES = [
  {
    id: 'client_loop',
    label: 'Client Loop',
    description: 'Client-safe updates, WhatsApp-ready messaging, and matter-linked follow-through.',
    mode: 'manual_or_scaffolded',
  },
  {
    id: 'team_ops',
    label: 'Team Ops',
    description: 'Junior/clerk tasks, review queues, and shared matter execution.',
    mode: 'live_in_product',
  },
  {
    id: 'drafting',
    label: 'Drafting',
    description: 'Court-ready drafting lanes with export/import hooks reserved for office-suite handoff.',
    mode: 'live_with_export_scaffold',
  },
];

const DEFAULT_GOVERNANCE = {
  trust_rule: 'Verified means official or policy-approved source-backed only.',
  manual_rule: 'Manual and scaffolded integrations stay explicitly labeled until credentials are connected.',
  audit_rule: 'User-entered chamber notes, client updates, and reviewed memory remain distinct from verified public law.',
};

const DEFAULT_DASHBOARD = {
  counts_by_stage: {},
  urgent_count: 0,
  hearing_this_week: 0,
  total_matters: 0,
  urgent_matters: [],
  matters: [],
};

const CLOSED_MATTER_STAGES = new Set(['closed', 'settled', 'archived']);
const PRIORITY_ORDER = {
  urgent: 4,
  high: 3,
  medium: 2,
  normal: 1,
  low: 0,
};

function cleanText(value, maxChars = 240) {
  return String(value || '').replace(/\s+/g, ' ').trim().slice(0, maxChars);
}

function cleanList(items, limit = 4) {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => cleanText(item, 180))
    .filter(Boolean)
    .slice(0, limit);
}

function normalizeStage(value) {
  return cleanText(value, 40).toLowerCase().replace(/\s+/g, '_');
}

function toTimestamp(value) {
  if (!value) return 0;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

function normalizeContinueItem(item, index) {
  return {
    ...item,
    id: cleanText(item?.id, 120) || `continue_${index + 1}`,
    kind: cleanText(item?.kind, 40).toUpperCase() || '',
    title: cleanText(item?.title, 180) || 'Resume work',
    summary: cleanText(item?.summary, 220) || 'Continue where you left off.',
    href: cleanText(item?.href, 320) || '/war-room',
    cta: cleanText(item?.cta, 80) || 'Continue',
    eyebrow: cleanText(item?.eyebrow, 80),
    updated_at: cleanText(item?.updated_at, 80),
    matter_id: cleanText(item?.matter_id, 80),
    matter_name: cleanText(item?.matter_name, 180),
  };
}

function isDraftResumeItem(item) {
  const href = cleanText(item?.href, 240);
  const text = [
    item?.kind,
    item?.eyebrow,
    item?.title,
    item?.summary,
  ].map((value) => cleanText(value, 160).toLowerCase()).join(' ');

  return href.startsWith('/documents')
    || href.startsWith('/drafting')
    || cleanText(item?.kind, 40).toUpperCase() === 'DOCUMENT'
    || text.includes('draft');
}

function priorityRank(value) {
  return PRIORITY_ORDER[cleanText(value, 40).toLowerCase()] ?? 1;
}

function healthRank(value) {
  const score = Number(value || 0);
  return score > 0 ? score : 999;
}

function buildMatterHomeCard(board, continueItems) {
  const matterId = cleanText(board?.matter_id, 80);
  const title = cleanText(board?.title || board?.matter_title || matterId || 'Matter workspace', 180);
  const resume = resolveMatterResumeTarget({
    matter: {
      ...board,
      matter_id: matterId,
      title,
    },
    continueItems,
  });

  return {
    matter_id: matterId,
    title,
    client_name: cleanText(board?.client_name, 120),
    court_name: cleanText(board?.court_name || board?.court_forum, 120),
    case_number: cleanText(board?.case_number, 120),
    summary: cleanText(board?.overview || board?.summary || board?.description || board?.recommended_action || 'Matter ready to continue.', 220)
      || 'Matter ready to continue.',
    stage: cleanText(board?.stage, 40) || 'active',
    stage_key: normalizeStage(board?.stage || 'active'),
    priority: cleanText(board?.priority, 40) || 'normal',
    health_score: Number(board?.health_score || 0),
    next_hearing_date: cleanText(board?.next_hearing_date, 80),
    updated_at: cleanText(board?.latest_activity_at || board?.updated_at || board?.created_at, 80),
    activity_sort: toTimestamp(board?.latest_activity_at || board?.updated_at || board?.created_at),
    route: cleanText(board?.route, 320) || (matterId ? `/war-room/${matterId}` : '/war-room'),
    continue_href: resume.href,
    continue_label: resume.cta || 'Continue',
    continue_summary: cleanText(resume.summary, 220) || 'Continue this matter.',
    continue_kind: cleanText(resume.kind, 40) || 'MATTER',
    continue_source: cleanText(resume.eyebrow, 80),
    has_resume: Boolean(resume.matched),
  };
}

function sortActiveMatters(left, right) {
  if (left.has_resume !== right.has_resume) return Number(right.has_resume) - Number(left.has_resume);
  if (priorityRank(left.priority) !== priorityRank(right.priority)) return priorityRank(right.priority) - priorityRank(left.priority);
  if (Boolean(left.next_hearing_date) !== Boolean(right.next_hearing_date)) return Number(Boolean(right.next_hearing_date)) - Number(Boolean(left.next_hearing_date));
  if (healthRank(left.health_score) !== healthRank(right.health_score)) return healthRank(left.health_score) - healthRank(right.health_score);
  return right.activity_sort - left.activity_sort;
}

function sortRecentMatters(left, right) {
  return right.activity_sort - left.activity_sort;
}

function buildDraftResumeCard(item) {
  return {
    id: cleanText(item?.id, 120) || item?.href || 'draft',
    title: cleanText(item?.title, 180) || 'Draft in progress',
    summary: cleanText(item?.summary, 220) || 'Continue this draft.',
    href: cleanText(item?.href, 320) || '/documents',
    cta: cleanText(item?.cta, 80) || 'Open draft',
    kind: cleanText(item?.kind, 40) || 'DOCUMENT',
    updated_at: cleanText(item?.updated_at, 80),
  };
}

function buildResumeLastWork({ continueItems, activeMatters, reentryPrompt }) {
  if (continueItems.length) {
    const lead = continueItems[0];
    return {
      title: cleanText(reentryPrompt?.title, 120) || lead.title || 'Resume last work',
      summary: cleanText(reentryPrompt?.description, 220) || lead.summary || 'Continue where you left off.',
      href: cleanText(reentryPrompt?.href, 320) || lead.href,
      cta: cleanText(reentryPrompt?.cta, 80) || lead.cta || 'Continue',
      eyebrow: lead.eyebrow || 'Resume',
      kind: lead.kind || 'MATTER',
    };
  }

  if (activeMatters.length) {
    const lead = activeMatters[0];
    return {
      title: lead.title || 'Resume matter',
      summary: lead.continue_summary || lead.summary || 'Continue this matter.',
      href: lead.continue_href || lead.route,
      cta: lead.continue_label || 'Continue',
      eyebrow: lead.continue_source || 'Resume matter',
      kind: lead.continue_kind || 'MATTER',
    };
  }

  return null;
}

function buildMatterHomeModel(summary = {}) {
  const continueItems = Array.isArray(summary?.continue_items)
    ? summary.continue_items.map(normalizeContinueItem)
    : [];
  const boards = Array.isArray(summary?.matter_boards) && summary.matter_boards.length
    ? summary.matter_boards
    : ((summary?.dashboard?.matters || []).map(buildMatterBoardFromDashboardItem));
  const matterCards = boards
    .map((board) => buildMatterHomeCard(board, continueItems))
    .filter((item) => item.matter_id || item.title);

  const activeMatters = matterCards
    .filter((item) => !CLOSED_MATTER_STAGES.has(item.stage_key))
    .sort(sortActiveMatters)
    .slice(0, 4);

  const activeMatterIds = new Set(activeMatters.map((item) => item.matter_id).filter(Boolean));
  let recentMatters = matterCards
    .filter((item) => !activeMatterIds.has(item.matter_id))
    .sort(sortRecentMatters)
    .slice(0, 4);

  if (!recentMatters.length) {
    recentMatters = [...matterCards].sort(sortRecentMatters).slice(0, 4);
  }

  const draftsInProgress = continueItems
    .filter(isDraftResumeItem)
    .map(buildDraftResumeCard)
    .slice(0, 4);

  return {
    active_matters: activeMatters,
    recent_matters: recentMatters,
    drafts_in_progress: draftsInProgress,
    resume_last_work: buildResumeLastWork({
      continueItems,
      activeMatters,
      reentryPrompt: summary?.reentry_prompt,
    }),
    start_new_matter: {
      href: '/war-room',
      cta: 'Start new matter',
      title: 'Start new matter',
      summary: 'Open a fresh matter.',
    },
  };
}

function normalizeRoleKey(user) {
  const raw = String(user?.practice_track || user?.role || '').trim().toUpperCase();
  return LEGACY_ROLE_ALIASES[raw] || raw || 'INDEPENDENT_LITIGATOR';
}

export function buildWorkflowRoleMeta(user) {
  const roleKey = normalizeRoleKey(user);
  return {
    role_key: roleKey,
    ...(ROLE_META[roleKey] || ROLE_META.INDEPENDENT_LITIGATOR),
  };
}

function buildIntegrationStatuses(user) {
  return DEFAULT_INTEGRATIONS.map((item) => {
    const connectedAt = user?.[`${item.id}_connected_at`];
    if (!connectedAt) return { ...item };
    return {
      ...item,
      mode: 'live',
      status_label: 'Live',
      connected_at: connectedAt,
      description: `${item.label} is connected for this workspace.`,
    };
  });
}

function buildMatterBoardFromDashboardItem(item) {
  const matterId = cleanText(item?.matter_id, 80);
  const nextSteps = cleanList([
    item?.next_step,
    item?.suggested_focus,
    item?.recommended_action,
  ], 3);

  return {
    matter_id: matterId,
    title: cleanText(item?.title || item?.name || matterId || 'Matter workspace', 180),
    client_name: cleanText(item?.client_name, 120),
    court_name: cleanText(item?.court_name || item?.court_forum, 120),
    case_number: cleanText(item?.case_number, 120),
    next_hearing_date: cleanText(item?.next_hearing_date, 80),
    health_score: Number(item?.health_score || 0),
    stage: cleanText(item?.stage, 40) || 'active',
    priority: cleanText(item?.priority, 40) || 'normal',
    latest_activity_at: cleanText(item?.latest_activity_at, 80),
    updated_at: cleanText(item?.updated_at, 80),
    created_at: cleanText(item?.created_at, 80),
    overview: cleanText(item?.summary || item?.description || item?.case_number || 'Matter is active and waiting for a fuller operating board.', 320),
    route: item?.route || (matterId ? `/war-room/${matterId}` : '/war-room'),
    next_steps: nextSteps,
    filing_readiness: {
      bundle_status: nextSteps.length ? 'scoping' : 'watch',
      bundle_label: nextSteps.length ? 'Readiness building' : 'Awaiting matter details',
      target_filing: cleanText(item?.target_filing, 160),
      checklist: [],
      defect_radar: [],
      output_bundle: [],
    },
    hearing_readiness: {
      score: Number(item?.hearing_readiness_score || 0),
      label: item?.next_hearing_date ? 'Hearing date surfaced' : 'Readiness building',
      ready_signals: [],
      missing_signals: [],
    },
    authority_ledger: {
      count: 0,
      cards: [],
      treatment_counts: {},
      verification_counts: {},
      forum_counts: {},
    },
    client_requests: [],
    junior_instructions: [],
    red_team_alerts: [],
    court_watch: {
      status_label: item?.next_hearing_date ? 'Date surfaced' : 'Watch building',
      tracking_mode: 'manual',
      sync_note: 'Using the stable matter board while the richer court-watch summary catches up.',
      tracking_items: nextSteps.map((step, index) => ({
        id: `${matterId || 'matter'}_watch_${index + 1}`,
        title: step,
        detail: 'Carry this inside the matter board until the richer court-watch layer is available.',
        status: 'watch',
      })),
      official_channels: [],
    },
  };
}

function buildLocalDashboard(dashboard) {
  return {
    ...DEFAULT_DASHBOARD,
    ...(dashboard || {}),
    urgent_matters: Array.isArray(dashboard?.urgent_matters) ? dashboard.urgent_matters : [],
    matters: Array.isArray(dashboard?.matters) ? dashboard.matters : [],
  };
}

function buildTodayFallback({ user, activation, dashboard }) {
  const role = buildWorkflowRoleMeta(user);
  const stableDashboard = buildLocalDashboard(dashboard);
  const matterBoards = (stableDashboard.matters || []).slice(0, 6).map(buildMatterBoardFromDashboardItem);
  const urgentQueue = (stableDashboard.urgent_matters || []).slice(0, 6);

  return {
    role,
    headline: `${role.home_label} for today's live matters`,
    dashboard: stableDashboard,
    continue_items: Array.isArray(activation?.continue_items) ? activation.continue_items : [],
    starter_workflows: Array.isArray(activation?.starter_workflows) ? activation.starter_workflows : [],
    reentry_prompt: activation?.reentry_prompt || {},
    matter_boards: matterBoards,
    urgent_queue: urgentQueue,
    hearings_this_week: matterBoards.filter((item) => item.next_hearing_date).slice(0, 6),
    active_matter_pressure: matterBoards.map((item) => ({
      matter_id: item.matter_id,
      matter_title: item.title,
      health_score: item.health_score,
      stage: item.stage,
      priority: item.priority,
      summary: item.overview,
      next_hearing_date: item.next_hearing_date,
      route: item.route,
    })),
    deltas: [],
    red_team_alerts: [],
    client_followups: [],
    team_tasks: [],
    integrations: buildIntegrationStatuses(user),
    governance: {
      trust_rule: DEFAULT_GOVERNANCE.trust_rule,
      manual_rule: DEFAULT_GOVERNANCE.manual_rule,
    },
    daily_brief: {},
  };
}

function buildBoardFallback({ user, dashboard }) {
  const role = buildWorkflowRoleMeta(user);
  const stableDashboard = buildLocalDashboard(dashboard);
  const matterBoards = (stableDashboard.matters || []).slice(0, 8).map(buildMatterBoardFromDashboardItem);

  return {
    role,
    headline: 'Board / Court Watch',
    dashboard: stableDashboard,
    matter_boards: matterBoards,
    court_watch_items: matterBoards.flatMap((board) => (
      (board.court_watch?.tracking_items || []).slice(0, 2).map((item) => ({
        matter_id: board.matter_id,
        matter_title: board.title,
        title: item.title,
        description: item.detail,
        status: item.status,
      }))
    )).slice(0, 8),
    morning_briefs: matterBoards
      .filter((board) => board.next_hearing_date)
      .map((board) => ({
        matter_id: board.matter_id,
        matter_title: board.title,
        hearing_date: board.next_hearing_date,
        court_name: board.court_name,
        readiness_label: board.hearing_readiness?.label,
        summary: board.overview,
        next_steps: board.next_steps || [],
      }))
      .slice(0, 8),
    post_hearing_recovery: matterBoards.flatMap((board) => (
      (board.next_steps || []).slice(0, 2).map((step) => ({
        matter_id: board.matter_id,
        matter_title: board.title,
        title: step,
      }))
    )).slice(0, 10),
  };
}

function buildAuthoritiesFallback({ user, registry }) {
  const metrics = registry?.metrics || {};
  return {
    role: buildWorkflowRoleMeta(user),
    headline: 'Authorities and verified public law layer',
    trust_guide: TRUST_GUIDE,
    registry_metrics: {
      source_family_count: Number(metrics.source_family_count || 0),
      adapter_ready_count: Number(metrics.adapter_ready_count || 0),
      material_types_supported: Array.isArray(metrics.material_types_supported) ? metrics.material_types_supported : [],
    },
    registry_highlights: Array.isArray(registry?.families) ? registry.families.slice(0, 8) : [],
    matter_authority_views: [],
    product_policy: registry?.product_policy || {
      verified_primary: 'Official court, statute, gazette, or regulator text only.',
      verified_secondary: 'Reporter-backed or verified secondary support should still be forum-tested.',
      portal_only: 'Official portal metadata can support procedure and status, but may not always be final reasoning text.',
      matter_files: 'Matter files and reviewed memory remain private record, not public law.',
    },
  };
}

function buildIntegrationsFallback({ user, registry }) {
  const metrics = registry?.metrics || {};
  return {
    role: buildWorkflowRoleMeta(user),
    integrations: buildIntegrationStatuses(user),
    verified_public_law: {
      source_family_count: Number(metrics.source_family_count || 0),
      adapter_ready_count: Number(metrics.adapter_ready_count || 0),
      material_types_supported: Array.isArray(metrics.material_types_supported) ? metrics.material_types_supported : [],
    },
    delivery_surfaces: DEFAULT_DELIVERY_SURFACES,
    governance: DEFAULT_GOVERNANCE,
  };
}

async function fetchJson(fetchImpl, url, headers) {
  try {
    const response = await fetchImpl(url, {
      headers,
      credentials: 'include',
    });
    const data = await response.json().catch(() => ({}));
    return {
      ok: response.ok,
      status: response.status,
      data,
    };
  } catch (error) {
    return {
      ok: false,
      status: 0,
      data: {},
      error,
    };
  }
}

function responseDetail(result) {
  return cleanText(result?.data?.detail || result?.data?.message || '', 220);
}

function isAccessFailure(result) {
  return result?.status === 401 || result?.status === 403;
}

function hasPayload(result) {
  return Boolean(result?.ok && result?.data && typeof result.data === 'object');
}

function compatibilityNotice(feature) {
  return `Showing a deployment-safe ${feature} view while the latest workflow summary routes finish syncing.`;
}

function offlineNotice(feature) {
  return `Showing the local ${feature} scaffold because the richer service response is not available yet.`;
}

export async function loadTodaySummary({ apiBase, headers, user, fetchImpl = fetch }) {
  const primary = await fetchJson(fetchImpl, `${apiBase}/today/summary`, headers);
  if (primary.ok) {
    return {
      data: {
        ...primary.data,
        matter_home: buildMatterHomeModel(primary.data),
      },
      notice: '',
    };
  }

  const [activation, dashboard] = await Promise.all([
    fetchJson(fetchImpl, `${apiBase}/activation-hub`, headers),
    fetchJson(fetchImpl, `${apiBase}/matters/dashboard/summary`, headers),
  ]);

  if (hasPayload(activation) || hasPayload(dashboard)) {
    const data = buildTodayFallback({
      user,
      activation: activation.data,
      dashboard: dashboard.data,
    });
    return {
      data: {
        ...data,
        matter_home: buildMatterHomeModel(data),
      },
      notice: compatibilityNotice('Today'),
    };
  }

  const data = buildTodayFallback({ user });
  return {
    data: {
      ...data,
      matter_home: buildMatterHomeModel(data),
    },
    notice: isAccessFailure(primary) ? responseDetail(primary) : offlineNotice('Today'),
  };
}

export async function loadBoardSummary({ apiBase, headers, user, fetchImpl = fetch }) {
  const primary = await fetchJson(fetchImpl, `${apiBase}/board/summary`, headers);
  if (primary.ok) {
    return { data: primary.data, notice: '' };
  }

  const dashboard = await fetchJson(fetchImpl, `${apiBase}/matters/dashboard/summary`, headers);
  if (hasPayload(dashboard)) {
    return {
      data: buildBoardFallback({ user, dashboard: dashboard.data }),
      notice: compatibilityNotice('Board / Court Watch'),
    };
  }

  return {
    data: buildBoardFallback({ user }),
    notice: isAccessFailure(primary) ? responseDetail(primary) : offlineNotice('Board / Court Watch'),
  };
}

export async function loadAuthoritiesOverview({ apiBase, headers, user, fetchImpl = fetch }) {
  const primary = await fetchJson(fetchImpl, `${apiBase}/authorities/overview`, headers);
  if (primary.ok) {
    return { data: primary.data, notice: '' };
  }

  const registry = await fetchJson(fetchImpl, `${apiBase}/verified-corpus/registry`, headers);
  if (hasPayload(registry)) {
    return {
      data: buildAuthoritiesFallback({ user, registry: registry.data }),
      notice: compatibilityNotice('Authorities'),
    };
  }

  return {
    data: buildAuthoritiesFallback({ user }),
    notice: isAccessFailure(primary) ? responseDetail(primary) : offlineNotice('Authorities'),
  };
}

export async function loadIntegrationsOverview({ apiBase, headers, user, fetchImpl = fetch }) {
  const primary = await fetchJson(fetchImpl, `${apiBase}/integrations/overview`, headers);
  if (primary.ok) {
    return { data: primary.data, notice: '' };
  }

  const registry = await fetchJson(fetchImpl, `${apiBase}/verified-corpus/registry`, headers);
  if (hasPayload(registry)) {
    return {
      data: buildIntegrationsFallback({ user, registry: registry.data }),
      notice: compatibilityNotice('integrations'),
    };
  }

  return {
    data: buildIntegrationsFallback({ user }),
    notice: isAccessFailure(primary) ? responseDetail(primary) : offlineNotice('integrations'),
  };
}
